import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setCompany(data.company || null);
    } catch {
      setUser(null);
      setCompany(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async () => {
    window.location.href = "/login";
  };

  // Email + password sign-in (new auth flow). Stores the bearer token, then
  // refreshes /auth/me so user/company are populated before navigating.
  const loginWithPassword = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data?.token) {
      try {
        localStorage.setItem("av_token", data.token);
      } catch (e) {
        /* localStorage unavailable — cookie session still applies */
      }
    }
    if (data?.user) setUser(data.user);
    await checkAuth();
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* ignore */
    }
    try {
      localStorage.removeItem("av_token");
    } catch (e) {
      /* ignore */
    }
    setUser(null);
    setCompany(null);
    window.location.href = "/";
  };

  const changePassword = async (currentPassword, newPassword) => {
    await api.post("/auth/change-password", {
      current_password: currentPassword,
      new_password: newPassword,
    });
    setUser((current) => (current ? { ...current, must_change_password: false } : current));
    await checkAuth();
  };

  const refreshCompany = async () => {
    try {
      const { data } = await api.get("/company");
      setCompany(data);
      return data;
    } catch {
      return null;
    }
  };

  return <AuthContext.Provider value={{ user, company, loading, login, loginWithPassword, changePassword, logout, setUser, setCompany, refreshCompany, checkAuth, refresh: checkAuth }}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
